import { defineStore } from 'pinia'
import { settingsAPI, stripeAPI } from '@/api'

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    profile: null,
    plans: null,
    isLoadingProfile: false,
    isLoadingPlans: false,
    isSubscribing: false,
    isChangingPassword: false,
    forgotEmailSent: false,
    error: null,
    message: null,
  }),

  getters: {
    email: (s) => s.profile?.email || null,
    name: (s) => s.profile?.name || null,
    licenseType: (s) => s.profile?.licenseType || 'free',
    isTrial: (s) => s.profile?.licenseType === 'trial',
    isPremium: (s) => s.profile?.licenseType === 'premium',
    isClub: (s) => s.profile?.licenseType === 'club',
    isFree: (s) => s.profile?.licenseType === 'free',
    isGoogleAuth: (s) => s.profile?.isGoogleAuth === true,
    canChangePassword: (s) => s.profile && !s.profile.isGoogleAuth,
    isEmailVerified: (s) => s.profile?.isEmailVerified === true,
    trialExpired: (s) => s.profile?.trialExpired === true,
    subscriptionStatus: (s) => s.profile?.subscriptionStatus || 'active',
    trialEndDate: (s) => {
      if (!s.profile?.trialEndDate) return null
      return new Date(s.profile.trialEndDate)
    },
    remainingTrialDays: (s) => {
      if (!s.profile?.trialEndDate || s.profile.licenseType !== 'trial') return null
      const end = new Date(s.profile.trialEndDate)
      const now = new Date()
      const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
      return diff > 0 ? diff : 0
    },
    hasStripeCustomer: (s) => !!s.profile?.stripeCustomerId,
    hasApp: (s) => s.profile?.hasApp === true,
    maxClubLicenses: (s) => s.profile?.maxClubLicenses || 0,
    isTrialOrBetter: (s) => ['trial', 'premium', 'club', 'club_coach'].includes(s.profile?.licenseType),
    isCurrentPlan: (s) => (id) => {
      const lic = s.profile?.licenseType
      if (!lic) return false
      if (id === lic) return true
      if (lic === 'club' && id.startsWith('club_')) {
        const tier = parseInt(id.split('_')[1], 10)
        const max = s.profile?.maxClubLicenses || 0
        return max > 0 && tier === max
      }
      return false
    },
  },

  actions: {
    async fetchProfile() {
      this.isLoadingProfile = true
      this.error = null
      try {
        const res = await settingsAPI.getProfile()
        this.profile = res.user
        if (res.user?.licenseType) {
          localStorage.setItem('dashboard_license', res.user.licenseType)
        }
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to load profile'
      } finally {
        this.isLoadingProfile = false
      }
    },

    async fetchPlans() {
      this.isLoadingPlans = true
      try {
        const res = await stripeAPI.getPlans()
        this.plans = res.plans
      } catch (error) {
        console.error('Failed to load plans:', error)
      } finally {
        this.isLoadingPlans = false
      }
    },

    async changePassword(newPassword) {
      this.isChangingPassword = true
      this.error = null
      this.message = null
      try {
        await settingsAPI.changePassword(newPassword)
        this.message = 'passwordChanged'
        return { success: true }
      } catch (error) {
        const msg = error.response?.data?.error || 'Error'
        this.error = msg
        return { success: false, error: msg }
      } finally {
        this.isChangingPassword = false
      }
    },

    async forgotPassword(email) {
      this.isLoadingProfile = true
      this.error = null
      this.message = null
      this.forgotEmailSent = false
      try {
        await settingsAPI.forgotPassword(email)
        this.forgotEmailSent = true
        this.message = 'forgotEmailSent'
        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.error || 'Error sending email'
        return { success: false, error: this.error }
      } finally {
        this.isLoadingProfile = false
      }
    },

    async subscribe(priceId) {
      this.isSubscribing = true
      this.error = null
      try {
        const res = await stripeAPI.createCheckout(priceId)
        if (res.url) {
          window.location.href = res.url
        }
        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.error || 'Payment error'
        return { success: false, error: this.error }
      } finally {
        this.isSubscribing = false
      }
    },

    async manageSubscription() {
      this.isSubscribing = true
      this.error = null
      try {
        const res = await stripeAPI.createPortalSession()
        if (res.url) {
          window.location.href = res.url
        }
        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.error || 'Portal error'
        return { success: false, error: this.error }
      } finally {
        this.isSubscribing = false
      }
    },

    clearMessages() {
      this.error = null
      this.message = null
    },
  },
})
